import { ReactNode, SyntheticEvent } from 'react'
import {
  Box,
  HStack,
  IconButton,
  Img,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  Tooltip,
  VStack
} from '@chakra-ui/react'
import imgPlaceholder from 'assets/images/missing_image.png'
import {
  DARK_MENU_ICON_COLOR,
  DARK_MENU_LIST_PROPS,
  DARK_MENU_TRIGGER_SX,
  DARK_TEXT_MUTED,
  DARK_TEXT_PRIMARY
} from 'constants/dark-tokens'
import { S3FileTypeEnum } from 'constants/aws'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import SvgIcon from 'components/SvgIcon'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { ICollection } from 'interfaces/collection'
import { IProcessWithRelations } from 'interfaces/process'
import { getS3FileUrl } from 'utils/images'
import { DarkColumn } from 'components/DarkTable/types'
import { IItemData } from './SearchResultsList'

interface BuildSearchResultColumnsArgs {
  isManageMode: boolean
  // Coach+ (non-basic user) — gates the share/make-copy/archive row actions.
  canManageActions: boolean
  // Bulk-action node rendered in the actions-column header while items are selected.
  manageActionsHeader?: ReactNode
}

function isProcessRow(row: IItemData): boolean {
  const item = row.originalItem as IEsGlobalSearchItem | undefined
  return item?.collectionName === EEsGlobalSearchCollection.PROCESS
}

// Featured-image (mainMedia) URL for a result row. Rows carry either an ES hit
// (mainMedia lives on correlatingData) or a raw process — same derivation the old
// card layout used. Returns undefined when the item has no featured image set.
function getRowThumbnailUrl(row: IItemData): string | undefined {
  const item = row.originalItem
  if (!item || typeof item !== 'object') return undefined

  const isEsItem = 'collectionName' in item && 'correlatingData' in item
  const data = isEsItem
    ? ((item as IEsGlobalSearchItem).correlatingData as IProcessWithRelations | ICollection | undefined)
    : (item as IProcessWithRelations)

  const mainMediaKey = data?.mainMedia
  if (!mainMediaKey) return undefined
  // getS3FileUrl falls back to the persisted active org when the id is 0/missing.
  return getS3FileUrl(S3FileTypeEnum.IMAGE, data?.organizationId ?? 0, mainMediaKey)
}

function onThumbnailError(evt: SyntheticEvent<HTMLImageElement, Event>): void {
  if (evt.currentTarget.src !== imgPlaceholder) {
    evt.currentTarget.src = imgPlaceholder
  }
}

// DarkTable columns for the global search results. Mixed process/collection rows share one
// column set; the Name cell shows the highlighted title on line 1 and the matched search
// snippet on line 2, matching the previous card layout on the dark surface.
export function buildSearchResultColumns(args: BuildSearchResultColumnsArgs): DarkColumn<IItemData>[] {
  const { isManageMode, canManageActions, manageActionsHeader } = args

  return [
    {
      key: 'name',
      header: 'Name',
      // Old-card layout: 80px featured image on the left ONLY when the item has one
      // (no reserved slot otherwise), 24px type icon inline with the title, matched
      // search snippet underneath.
      render: row => {
        const thumbnailUrl = getRowThumbnailUrl(row)
        return (
          <HStack align="flex-start" spacing={{ base: 2, md: 4 }} minW={0}>
            {thumbnailUrl ? (
              <Img
                width={{ base: '56px', md: '80px' }}
                height={{ base: '56px', md: '80px' }}
                flexShrink={0}
                src={thumbnailUrl}
                onError={onThumbnailError}
                borderRadius="8px"
                objectFit="cover"
              />
            ) : null}
            <VStack spacing="2px" align="stretch" minW={0} flex={1}>
              <HStack align="center" spacing={{ base: 2, md: 3 }} minW={0}>
                {row.leadingIcon && (
                  <Box
                    width="24px"
                    height="24px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    sx={{ '& svg': { width: '24px', height: '24px' } }}
                  >
                    {row.leadingIcon}
                  </Box>
                )}
                <Box
                  color={DARK_TEXT_PRIMARY}
                  fontWeight={500}
                  fontSize="14px"
                  minW={0}
                  sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                >
                  {row.title}
                </Box>
              </HStack>
              {row.subtitle ? (
                <Box
                  color={DARK_TEXT_MUTED}
                  fontSize="14px"
                  minW={0}
                  sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                >
                  {row.subtitle}
                </Box>
              ) : null}
            </VStack>
          </HStack>
        )
      }
    },
    {
      key: 'type',
      header: '',
      align: 'right',
      // Fits both tags on one line: "View Only" + "Stack" at 14px need ~152px,
      // and DarkTable spends 32px of every cell on its own horizontal padding.
      width: '200px',
      hideOnMobile: true,
      render: row => {
        const isProcess = isProcessRow(row)
        return (
          <HStack spacing={2} justify="flex-end">
            {!row.isEditor && (
              <Tag
                variant="solid"
                background="whiteAlpha.200"
                color={DARK_TEXT_MUTED}
                borderRadius="6px"
                fontSize="14px"
                paddingY={0.5}
                paddingX={2}
                whiteSpace="nowrap"
                flexShrink={0}
              >
                View Only
              </Tag>
            )}
            <Tag
              variant="solid"
              background={isProcess ? '#00A9EB' : '#4CA817'}
              color="white"
              borderRadius="6px"
              fontSize="14px"
              paddingY={0.5}
              paddingX={2}
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {isProcess ? 'Stack' : 'Collection'}
            </Tag>
          </HStack>
        )
      }
    },
    {
      key: 'actions',
      header: manageActionsHeader ?? '',
      align: 'right',
      // Two `size="sm"` icon buttons are 32px each: 72px is the real content
      // width on a phone, where the desktop 96px was pure dead space.
      width: { base: '72px', md: '96px' },
      render: row => {
        const canShare = isManageMode && canManageActions && row.canShowShare && !!row.onShare
        const canMakeCopy = isManageMode && canManageActions && row.canShowMakeCopy && row.isEditor && !!row.onMakeCopy
        const canArchive = isManageMode && canManageActions && row.canShowArchive && !!row.onArchive
        // Remove unlinks a Stack from the Collection being managed; it is not gated on
        // canManageActions because the Collection page already decides who may edit.
        const canRemove = isManageMode && row.canShowRemove && !!row.onRemove
        const hasMenu = canShare || canMakeCopy || canArchive || canRemove

        return (
          <HStack justify="flex-end" spacing={1} onClick={e => e.stopPropagation()}>
            <Tooltip
              label="Quick view"
              height="36px"
              fontSize="14px"
              padding={2}
              background="gray.700"
              placement="top"
              color="white"
              hasArrow
              borderRadius="4px"
            >
              <IconButton
                aria-label="Quick view"
                variant="ghost"
                size="sm"
                sx={DARK_MENU_TRIGGER_SX}
                icon={<SvgIcon iconName="ic_detail" size={18} color={DARK_MENU_ICON_COLOR} />}
                onClick={() => row.onQuickView?.()}
              />
            </Tooltip>
            {hasMenu && (
              <Menu placement="bottom-end" autoSelect={false} isLazy strategy="fixed">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  variant="ghost"
                  size="sm"
                  sx={DARK_MENU_TRIGGER_SX}
                  icon={<SvgIcon iconName="show-more-icon" size={18} color={DARK_MENU_ICON_COLOR} />}
                />
                <MenuList minWidth="180px" zIndex={9999} {...DARK_MENU_LIST_PROPS}>
                  {canShare && (
                    <MenuItem
                      border="none"
                      bg="transparent"
                      color={DARK_TEXT_PRIMARY}
                      _hover={{ bg: 'rgba(255,255,255,0.08)' }}
                      _focus={{ bg: 'rgba(255,255,255,0.08)' }}
                      icon={<SvgIcon iconName="ic_share" size={15} color={DARK_MENU_ICON_COLOR} />}
                      onClick={() => row.onShare?.()}
                    >
                      Share
                    </MenuItem>
                  )}
                  {canMakeCopy && (
                    <MenuItem
                      border="none"
                      bg="transparent"
                      color={DARK_TEXT_PRIMARY}
                      _hover={{ bg: 'rgba(255,255,255,0.08)' }}
                      _focus={{ bg: 'rgba(255,255,255,0.08)' }}
                      icon={<SvgIcon iconName="copy" size={15} color={DARK_MENU_ICON_COLOR} />}
                      onClick={() => row.onMakeCopy?.()}
                    >
                      Make a copy
                    </MenuItem>
                  )}
                  {canArchive && (
                    <MenuItem
                      border="none"
                      bg="transparent"
                      color={DARK_TEXT_PRIMARY}
                      _hover={{ bg: 'rgba(255,255,255,0.08)' }}
                      _focus={{ bg: 'rgba(255,255,255,0.08)' }}
                      icon={<SvgIcon iconName="archive" size={15} color={DARK_MENU_ICON_COLOR} />}
                      onClick={() => row.onArchive?.()}
                    >
                      Archive {isProcessRow(row) ? 'stack' : 'collection'}
                    </MenuItem>
                  )}
                  {canRemove && (
                    <MenuItem
                      border="none"
                      bg="transparent"
                      color={DARK_TEXT_PRIMARY}
                      _hover={{ bg: 'rgba(255,255,255,0.08)' }}
                      _focus={{ bg: 'rgba(255,255,255,0.08)' }}
                      icon={<SvgIcon iconName="material-symbols_link-off" size={15} color={DARK_MENU_ICON_COLOR} />}
                      onClick={() => row.onRemove?.()}
                    >
                      Remove from collection
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            )}
          </HStack>
        )
      }
    }
  ]
}
